import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Dropdown, Input, Menu, message, Select } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import StatusButton from 'components/StatusButton';
import { useRef, useState } from 'react';
import insertAtCursor from 'utils/insertAtCursor';
import { getItems } from '../service';

function BatchSelect({
  filter,
  onSelect,
}: {
  filter: Omit<ReviewAnalysis.OverviewFilter, 'spuIds'>;
  onSelect: (items: ReviewAnalysis.Item[]) => void;
}) {
  const { t } = useTranslate();

  const [text, setText] = useState('');

  const [selectType, setSelectType] = useState<2 | 3>(2);

  const textareaRef = useRef<TextAreaRef>(null);

  const onTextChange = (value: string) => {
    const values = value.split('\n');
    const validValue = values.slice(0, 100).join('\n');
    setText(validValue);
  };

  const onPaste = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const { current: textarea } = textareaRef;

        if (textarea && text) {
          const { resizableTextArea } = textarea;

          if (resizableTextArea) {
            const { textArea } = resizableTextArea;

            const nextValue = insertAtCursor(textArea, text);

            onTextChange(nextValue);
          }
        }
      })
      .catch(() => {});
  };

  const onQuickSelect = () => {
    var ids = text.split('\n').filter(Boolean);
    ids = ids.map((e) => e?.trim());

    const { spuKeys, storeInfo, ...otherFilters } = filter;

    return getItems({
      keyword: ids.join('\n'),
      keywordType: selectType,
      pageInfo: {
        page: 1,
        pageSize: 10000,
      },
      overviewRequest: {
        ...otherFilters,
        storeIds: storeInfo.list?.reduce<string[]>((res, { id }) => [...res, id], []) || [],
      },
    })
      .then(({ itemData }) => {
        const unfoundIds = ids.filter((id) => {
          return itemData.every((item) => {
            const { spuId, itemSku } = item;

            return id !== (selectType === 2 ? spuId : itemSku);
          });
        });
        if (unfoundIds.length > 0) {
          message.warn(t('aide.x项未找到对应商品', { x: unfoundIds.length }));
          setText(unfoundIds.join('\n'));
        } else {
          message.success(t('aide.已选中x项', { x: itemData.length }));
          setText('');
        }
        onSelect(itemData);
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      });
  };

  return (
    <Container>
      <header>
        <span>{t('aide.选择类型')}：</span>
        <Select
          value={selectType}
          onChange={setSelectType}
          options={[
            { value: 2, label: t('sider.商品ID') },
            { value: 1, label: t('sider.商品名称') },
            { value: 3, label: t('sider.货号') },
          ]}
        />
      </header>
      <section>
        <Dropdown
          trigger={['contextMenu']}
          overlay={
            <Menu>
              <Menu.Item onClick={onPaste}>{t('ViewsChat.粘贴')}</Menu.Item>
            </Menu>
          }
        >
          <Input.TextArea
            ref={textareaRef}
            placeholder={t('review-item-batch-select-tips')}
            rows={22}
            value={text}
            onChange={({ target: { value } }) => onTextChange(value)}
          />
        </Dropdown>
        <footer>
          <StatusButton
            block
            type="primary"
            disabled={!text || !text.trim()}
            onClick={onQuickSelect}
          >
            {t('knowledge.item.select.mode.mult')}
          </StatusButton>
        </footer>
      </section>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    padding: 8px 24px;
    border-bottom: solid 1px #f0f2f5;
    > .ant-select {
      width: 240px;
    }
  }
  > section {
    flex: 1 0 0;
    padding: 12px 14px;
    textarea {
      flex: 1 0 0;
      resize: none;
    }
    > footer {
      flex: 0 0 auto;
      padding: 8px;
      border: solid 1px #d9d9d9;
      border-top: none;
    }
  }
`;

export default BatchSelect;
